import Loading from '@/shared/components/Carregando';
import { ColaboradorPorSetorPdf } from './reports/ColaboradorPorSetor.report';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { PeriodoLotacaoColaboradorTexto } from './relatorio-colaboradores.utils';
import { Fragment, useEffect, useState } from 'react';
import { GetRelatorioColaboradoresPorSetor } from './relatorio-colaboradores.service';
import {
  RelatorioColaboradorItem,
  RelatorioComarcasColaboradores,
  RelatorioPredioColaboradores,
} from './relatorio-colaboradores.types';
import { toast } from 'react-toastify';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';

export default function RelatorioColaboradoresPorSetorPage() {
  const { setActions } = useBreadcrumbActions();
  const [loading, setLoading] = useState(true);
  const [relatorio, setRelatorio] = useState<RelatorioComarcasColaboradores[]>([]);

  useEffect(() => {
    GetRelatorioColaboradoresPorSetor()
      .then((result) => {
        setRelatorio(Array.isArray(result) ? result : []);
        setLoading(false);
      })
      .catch((res) => {
        const mensagem =
          res instanceof Error ? res.message : 'Falha ao buscar relatório de colaboradores por setor.';
        toast.error(mensagem);
        setRelatorio([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setActions({
      print: {
        label: 'Imprimir',
        onClick: () => {
          void exportFromJsxToPdf(<ColaboradorPorSetorPdf comarcas={relatorio} />).catch(
            (err) => {
              toast.error('Falha ao gerar o PDF: ' + String(err));
            },
          );
        },
      },
    });
    return () => setActions({});
  }, [setActions, relatorio]);

  function RenderizarColunaEquipe(colaboradores: RelatorioColaboradorItem[]) {
    const elementos: JSX.Element[] = [];

    if (!colaboradores || colaboradores.length === 0) {
      elementos.push(
        <tr key="equipe-vazio">
          <td className="text-start">&nbsp;</td>
        </tr>,
      );
      return elementos;
    }

    colaboradores.forEach((colaborador, index) => {
      const periodo = PeriodoLotacaoColaboradorTexto(
        colaborador.lotacoes_data_inicial,
        colaborador.lotacoes_data_final,
      );
      const classname = colaborador.diretor ? 'fw-bold ' : '';
      elementos.push(
        <tr key={`colaborador-${colaborador.colaborador_id}-${index}`}>
          <td className={`text-start ${classname}`.trim()}>
            {colaborador.colaborador_nome}
            {periodo ? (
              <>
                <br />
                <span className="text-muted small fw-normal">{periodo}</span>
              </>
            ) : null}
          </td>
        </tr>,
      );
    });

    return elementos;
  }

  function RenderizarSetoresDoPredio(predio: RelatorioPredioColaboradores) {
    return (predio.setores ?? []).map((setor, setorIndex) => {
      const colunaEquipe = RenderizarColunaEquipe(setor.colaboradores);
      return (
        <Fragment key={`setor-${setor.id}-${setorIndex}`}>
          <tr>
            <td rowSpan={colunaEquipe.length + 1} className="text-start">
              {setor.nome}
            </td>
          </tr>
          {colunaEquipe}
        </Fragment>
      );
    });
  }

  function totalEquipe(predio: RelatorioPredioColaboradores) {
    return (predio.setores ?? []).reduce(
      (acc, setor) => acc + (setor.colaboradores?.length || 0),
      0,
    );
  }

  function RenderizarPrediosDaComarca(comarca: RelatorioComarcasColaboradores) {
    const elementos: JSX.Element[] = [];

    if (!comarca.predios || comarca.predios.length <= 0) return elementos;

    comarca.predios.forEach((predio, index) => {
      if (!predio.setores || predio.setores.length === 0) return;

      const total = totalEquipe(predio);

      elementos.push(
        <Fragment key={`predio-${index}`}>
          <table className="table table-som table-bordered align-middle border border-dark m-0 p-0">
            <thead>
              <tr>
                <th>{predio.predio_nome}</th>
              </tr>
              <tr>
                <td className="relatorio-estagiarios-titulovagas">
                  <div className="d-flex justify-content-between align-items-baseline gap-3 flex-wrap">
                    <span className="text-start small fst-italic fw-normal">
                      {''.concat(
                        predio.endereco_logradouro || '',
                        ', nº ',
                        (predio.endereco_numero || '') + ', ' + (predio.endereco_bairro || ''),
                        '. CEP: ',
                        predio.endereco_cep || '',
                        '. ',
                        predio.endereco_cidade || '',
                        ' - ',
                      )}
                      {'('.concat(
                        predio.telefoneprincipal_ddd || '',
                        ') ',
                        predio.telefoneprincipal_numero || '',
                      )}
                    </span>
                    <span className="text-nowrap">Total: {total}</span>
                  </div>
                </td>
              </tr>
            </thead>
          </table>
          <table className="table table-sm table-bordered align-middle border border-dark text-start mb-4">
            <thead>
              <tr className="text-center">
                <th style={{ width: '60%' }}>SETOR DE ATUAÇÃO</th>
                <th style={{ width: '40%' }}>EQUIPE</th>
              </tr>
            </thead>
            <tbody>{RenderizarSetoresDoPredio(predio)}</tbody>
          </table>
        </Fragment>,
      );
    });

    return elementos;
  }

  if (loading) return <Loading />;

  const comarcasVisiveis = (Array.isArray(relatorio) ? relatorio : []).filter(
    (comarca) =>
      Array.isArray(comarca.predios) &&
      comarca.predios.some(
        (predio) => Array.isArray(predio.setores) && predio.setores.length > 0,
      ),
  );

  return (
    <div className="row">
      <div className="col-12 d-print-none">
        <h3>Colaboradores por Setor</h3>
        <span className="blockquote-footer pb-4 text-end">
          Lotações vigentes de colaboradores agrupadas por comarca, prédio e setor.
        </span>
        <hr className="pb-4" />
      </div>

      <div className="col-12" id="conteudo">
        <div className="text-center">
          <h4 className="text-center relatorio-font relatorio-titulo">
            DEFENSORIA PÚBLICA DO ESTADO DE SERGIPE - TABELA DE LOTAÇÃO
          </h4>
        </div>
        <br />

        {comarcasVisiveis.length === 0 ? (
          <p className="text-center text-muted">Nenhum setor encontrado.</p>
        ) : (
          comarcasVisiveis.map((comarca, index) => (
            <div key={index} className="relatorio">
              <h5 className="relatorio-estagiarios-comarca">{comarca.comarca_nome}</h5>
              {RenderizarPrediosDaComarca(comarca)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
